import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import ListTypeDetailHorizontal from '@/components/feature/list-type/list-type-detail-horizontal';
import ListTypeDetailVertical from '@/components/feature/list-type/list-type-detail-vertical';
import ListTypeQrModal from '@/components/feature/list-type/list-type-qr-modal';
import { BuildingMenuType } from '@/enums';
import { FloorGuideType } from '@/enums/floor-guide';
import { useOrientation } from '@/hooks/useOrientation';
import { PATHS } from '@/router/paths';
import { useConfigStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styles from './ListTypeDetailPage.module.scss';
import { useCustomMenuContentById } from '@/hooks/custom-menu';
import type { ListTypeListItem } from '@/types/list-type';

const ListTypeDetailPage = () => {
    const { gnbMenuId = '', id = '' } = useParams<{ gnbMenuId: string; id: string }>();
    const navigate = useNavigate();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { isHorizontal } = useOrientation();
    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();

    const [isQrOpen, setIsQrOpen] = useState(false);

    const detail = useCustomMenuContentById(gnbMenuId, id);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        return listMenus.find((menu) => menu.id === gnbMenuId && menu.menuType === BuildingMenuType.CUSTOM_MENU);
    }, [server?.buildingMenus, ui.language, gnbMenuId]);

    // Redirect if item not found
    if (!detail) {
        return <Navigate to={`${PATHS.CUSTOM_MENU.LIST_TYPE}/${gnbMenuId}`} replace />;
    }

    const handleBack = () => {
        navigate(`${PATHS.CUSTOM_MENU.LIST_TYPE}/${gnbMenuId}`);
    };

    const handleDirection = (detail: ListTypeListItem) => {
        if (!detail.poiId || !buildings) return;

        setSelectedPoi({
            studioFloorID: detail.floorId,
            studioPoiId: detail.poiId,
        });

        setFloorGuideType(FloorGuideType.DIRECTION);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(detail.floorId, detail.poiId));
    };

    const pageClassname = clsx(styles.detail, {
        [styles['detail__vertical']]: !isHorizontal,
        [styles['detail__horizontal']]: isHorizontal,
        [styles['detail__contrast']]: ui.contrast,
        [styles['detail__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
        [styles['detail__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
    });

    const sharedProps = {
        detail,
        onBack: handleBack,
        onQrClick: () => setIsQrOpen(true),
        onDirectionClick: handleDirection,
    };

    return (
        <div className={pageClassname}>
            {!isHorizontal && (
                <div
                    className={clsx(styles['detail__header'], {
                        [styles['detail__header--vertical']]: !isHorizontal,
                        [styles['detail__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                    })}
                >
                    <ShopFnbFacilityHeader
                        title={(currentMenu?.name as string) || ''}
                        description={currentMenu?.description || ''}
                        isHorizontal={false}
                        tabIndex={1}
                    />
                </div>
            )}

            <div className={styles['detail__body']}>
                {isHorizontal ? <ListTypeDetailHorizontal {...sharedProps} /> : <ListTypeDetailVertical {...sharedProps} />}
            </div>

            {isQrOpen && <ListTypeQrModal isOpen={isQrOpen} qrUrl={detail.qrUrl} onClose={() => setIsQrOpen(false)} />}
        </div>
    );
};

export default ListTypeDetailPage;
